/**
 * Syntaxe du contenu des cartes (ADR 0008) : Markdown (CommonMark, barré de GFM) et trois
 * extensions, partagées par l'affichage des cartes et par l'éditeur.
 *
 * - Formule : `$…$` dans la ligne, `$$…$$` en bloc (LaTeX, rendu par KaTeX).
 * - Surlignage : `==texte==` (jaune), `=={violet}texte==` (couleur de base : violet, bleu,
 *   rouge, jaune, vert) ou `=={#cdb4db}texte==` (couleur personnelle).
 * - Trou : `{{n::réponse}}`, ou `{{n::réponse::indice}}` (voir src/cartes/trous.ts) ; la
 *   réponse peut être mise en forme et contenir des accolades équilibrées.
 */
import {
  parser as parserMarkdown,
  Strikethrough,
  type BlockContext,
  type InlineContext,
  type Line,
  type MarkdownConfig,
} from '@lezer/markdown';
import { trouverFermeture } from '../cartes/trous';

const DOLLAR = 36; // $
const EGAL = 61; // =
const ACCOLADE = 123; // {
const BARRE_OBLIQUE_INVERSE = 92; // \

const estEspace = (code: number) =>
  code === 32 || code === 9 || code === 10 || code === 13 || Number.isNaN(code);

/** Couleurs de surlignage de base (design system, surligne-*). */
export const COULEURS_DE_BASE = ['violet', 'bleu', 'rouge', 'jaune', 'vert'] as const;
export type CouleurDeBase = (typeof COULEURS_DE_BASE)[number];

/** Couleur valide d'un surlignage : une couleur de base ou une couleur #rgb / #rrggbb. */
export function couleurValide(couleur: string): boolean {
  return (
    (COULEURS_DE_BASE as readonly string[]).includes(couleur) ||
    /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(couleur)
  );
}

/** Fin d'une formule commençant à `debut` (après le ou les `$` ouvrants), ou -1. */
function finFormule(cx: InlineContext, debut: number, double: boolean): number {
  for (let i = debut; i < cx.end; i++) {
    const c = cx.char(i);
    if (c === BARRE_OBLIQUE_INVERSE) {
      i++;
      continue;
    }
    if (c !== DOLLAR) continue;
    if (double) {
      if (cx.char(i + 1) === DOLLAR) return i;
      continue;
    }
    // `$` fermant : précédé d'un caractère non blanc, pas suivi d'un chiffre (« 5 $ et 10 $ »).
    if (estEspace(cx.char(i - 1))) continue;
    const suivant = cx.char(i + 1);
    if (suivant >= 48 && suivant <= 57) continue;
    return i;
  }
  return -1;
}

const Formule: MarkdownConfig = {
  defineNodes: [
    { name: 'Formule' },
    { name: 'FormuleBloc', block: true },
    { name: 'FormuleMarque' },
    { name: 'FormuleTexte' },
  ],
  parseInline: [
    {
      name: 'Formule',
      before: 'Escape',
      parse(cx, next, pos) {
        if (next !== DOLLAR) return -1;
        const double = cx.char(pos + 1) === DOLLAR;
        const debut = pos + (double ? 2 : 1);
        if (!double && estEspace(cx.char(debut))) return -1;
        const fin = finFormule(cx, debut, double);
        if (fin <= debut) return -1;
        const finMarque = fin + (double ? 2 : 1);
        return cx.addElement(
          cx.elt(double ? 'FormuleBloc' : 'Formule', pos, finMarque, [
            cx.elt('FormuleMarque', pos, debut),
            cx.elt('FormuleTexte', debut, fin),
            cx.elt('FormuleMarque', fin, finMarque),
          ]),
        );
      },
    },
  ],
  parseBlock: [
    {
      name: 'FormuleBloc',
      before: 'FencedCode',
      parse(cx: BlockContext, line: Line) {
        if (!line.text.startsWith('$$', line.pos)) return false;
        const debut = cx.lineStart + line.pos;
        const reste = line.text.slice(line.pos + 2);
        const marques = [cx.elt('FormuleMarque', debut, debut + 2)];
        // Sur une seule ligne : $$ … $$
        const fermeture = reste.indexOf('$$');
        if (fermeture !== -1) {
          if (reste.slice(fermeture + 2).trim() !== '') return false;
          const finTexte = debut + 2 + fermeture;
          cx.nextLine();
          cx.addElement(
            cx.elt('FormuleBloc', debut, finTexte + 2, [
              ...marques,
              cx.elt('FormuleTexte', debut + 2, finTexte),
              cx.elt('FormuleMarque', finTexte, finTexte + 2),
            ]),
          );
          return true;
        }
        let fin = cx.lineStart + line.text.length;
        const debutTexte = debut + 2;
        let finTexte = fin;
        while (cx.nextLine()) {
          const i = line.text.indexOf('$$');
          if (i !== -1) {
            finTexte = cx.lineStart + i;
            fin = finTexte + 2;
            cx.nextLine();
            break;
          }
          finTexte = fin = cx.lineStart + line.text.length;
        }
        const enfants = [
          ...marques,
          cx.elt('FormuleTexte', debutTexte, Math.max(debutTexte, finTexte)),
        ];
        if (fin > finTexte) enfants.push(cx.elt('FormuleMarque', finTexte, fin));
        cx.addElement(cx.elt('FormuleBloc', debut, fin, enfants));
        return true;
      },
    },
  ],
};

const DelimiteurSurlignage = { resolve: 'Surlignage', mark: 'SurlignageMarque' };

const Surlignage: MarkdownConfig = {
  defineNodes: [{ name: 'Surlignage' }, { name: 'SurlignageMarque' }],
  parseInline: [
    {
      name: 'Surlignage',
      parse(cx, next, pos) {
        if (next !== EGAL || cx.char(pos + 1) !== EGAL || cx.char(pos - 1) === EGAL) return -1;
        let fin = pos + 2;
        let couleur = false;
        if (cx.char(fin) === ACCOLADE) {
          const fermeture = cx.slice(fin, Math.min(cx.end, fin + 40)).indexOf('}');
          if (fermeture > 1 && couleurValide(cx.slice(fin + 1, fin + fermeture))) {
            fin += fermeture + 1;
            couleur = true;
          }
        }
        if (cx.char(fin) === EGAL) return -1;
        const avant = cx.char(pos - 1);
        const apres = cx.char(fin);
        const ouvre = !estEspace(apres);
        const ferme = !couleur && !estEspace(avant);
        if (!ouvre && !ferme) return -1;
        return cx.addDelimiter(DelimiteurSurlignage, pos, fin, ouvre, ferme);
      },
    },
  ],
};

const Trou: MarkdownConfig = {
  defineNodes: [
    { name: 'Trou' },
    { name: 'TrouMarque' },
    { name: 'TrouNumero' },
    { name: 'TrouIndice' },
  ],
  parseInline: [
    {
      name: 'Trou',
      before: 'Formule',
      parse(cx, next, pos) {
        if (next !== ACCOLADE || cx.char(pos + 1) !== ACCOLADE) return -1;
        const texte = cx.slice(pos, cx.end);
        const fermeture = trouverFermeture(texte, 2);
        if (fermeture === -1) return -1;
        const fin = pos + fermeture + 2;
        const enfants = [cx.elt('TrouMarque', pos, pos + 2)];
        let debut = pos + 2;
        const numero = /^[1-9][0-9]{0,5}::/.exec(texte.slice(2, fermeture));
        if (numero) {
          enfants.push(cx.elt('TrouNumero', debut, debut + numero[0].length));
          debut += numero[0].length;
        }
        // Indice : dernier « :: » hors accolades et formules.
        const interieur = cx.slice(debut, fin - 2);
        const indice = positionIndice(interieur);
        const finReponse = indice === -1 ? fin - 2 : debut + indice;
        enfants.push(...cx.parser.parseInline(cx.slice(debut, finReponse), debut));
        if (indice !== -1) enfants.push(cx.elt('TrouIndice', finReponse, fin - 2));
        enfants.push(cx.elt('TrouMarque', fin - 2, fin));
        return cx.addElement(cx.elt('Trou', pos, fin, enfants));
      },
    },
  ],
};

/** Position du `::` qui sépare la réponse de l'indice, hors accolades et formules ; -1 sinon. */
export function positionIndice(interieur: string): number {
  let profondeur = 0;
  let formule = false;
  let position = -1;
  for (let i = 0; i < interieur.length; i++) {
    const c = interieur[i];
    if (c === '\\') i++;
    else if (c === '$') formule = !formule;
    else if (!formule && c === '{') profondeur++;
    else if (!formule && c === '}') profondeur = Math.max(0, profondeur - 1);
    else if (!formule && profondeur === 0 && c === ':' && interieur[i + 1] === ':') {
      position = i;
      i++;
    }
  }
  return position;
}

/** Extensions des cartes sans trous (basique, masquages) : un `{{…}}` y reste du texte. */
export const EXTENSIONS_SANS_TROUS = [Strikethrough, Formule, Surlignage];

/** Extensions des cartes de texte à trous, pour @lezer/markdown et @codemirror/lang-markdown. */
export const EXTENSIONS_CARTES = [...EXTENSIONS_SANS_TROUS, Trou];

/** Analyseur du contenu des cartes de texte à trous. */
export const analyseurCartes = parserMarkdown.configure(EXTENSIONS_CARTES);

/** Analyseur des autres cartes. */
export const analyseurSansTrous = parserMarkdown.configure(EXTENSIONS_SANS_TROUS);

/**
 * Nombre de trous `{{…}}` que l'affichage reconnaît dans le texte. Il peut être inférieur au
 * nombre de trous saisis quand une formule mal fermée les englobe (`$x = $ {{1::y}}` : un `$`
 * fermant ne suit pas une espace, la formule court donc jusqu'au `$` suivant).
 */
export function trousAffiches(texte: string): number {
  let nombre = 0;
  analyseurCartes.parse(texte).iterate({
    enter(noeud) {
      if (noeud.name === 'Trou') nombre += 1;
    },
  });
  return nombre;
}
